import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const inputStyle = {
  width: '100%',
  maxWidth: '400px',
  padding: '10px',
  fontSize: '16px',
  boxSizing: 'border-box'
};

const errorTextStyle = {
  color: '#c53030',
  margin: '4px 0 0 0'
};

const SERVER_FIELD_MESSAGES = {
  name: 'Enter a valid name.',
  email: 'Enter a valid email address.',
  phone: 'Enter a valid phone number.',
  answers: 'There was a problem with your answers. Please try again.'
};

const InductionAssessment = () => {
  const navigate = useNavigate();
  const [quizStatus, setQuizStatus] = useState('loading'); // 'loading' | 'error' | 'ready'
  const [quiz, setQuiz] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [answers, setAnswers] = useState({});

  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const summaryRef = useRef(null);

  const loadQuiz = () => {
    setQuizStatus('loading');
    fetch('/api/induction-quiz')
      .then((res) => {
        if (!res.ok) {
          throw new Error('induction-quiz request failed');
        }
        return res.json();
      })
      .then((data) => {
        setQuiz(data);
        setQuizStatus('ready');
      })
      .catch(() => {
        setQuizStatus('error');
      });
  };

  useEffect(() => {
    loadQuiz();
  }, []);

  useEffect(() => {
    if (errors.length > 0 && summaryRef.current) {
      summaryRef.current.focus();
    }
  }, [errors]);

  const handleAnswerChange = (questionId, key) => {
    setAnswers((prev) => ({ ...prev, [questionId]: key }));
  };

  const errorFor = (id) => errors.find((problem) => problem.id === id);

  const focusField = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.focus();
      el.scrollIntoView({ block: 'center' });
    }
  };

  const validate = () => {
    const problems = [];

    if (!name.trim()) {
      problems.push({ id: 'name', message: 'Enter your name.' });
    }
    if (!email.trim()) {
      problems.push({ id: 'email', message: 'Enter your email address.' });
    }
    if (!phone.trim()) {
      problems.push({ id: 'phone', message: 'Enter your phone number.' });
    }

    if (quiz) {
      quiz.questions.forEach((question, index) => {
        if (!answers[question.id]) {
          problems.push({
            id: `question-${question.id}`,
            message: `Answer question ${index + 1}.`
          });
        }
      });
    }

    return problems;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const problems = validate();
    setErrors(problems);

    if (problems.length > 0) {
      return;
    }

    setSubmitError(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/induction-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, answers, website })
      });

      if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        const fields = Array.isArray(data.fields) ? data.fields : [];
        setErrors(fields.map((field) => ({
          id: field,
          message: SERVER_FIELD_MESSAGES[field] || 'Check this field and try again.'
        })));
        setSubmitting(false);
        return;
      }

      if (!res.ok) {
        throw new Error('induction-submit request failed');
      }

      const result = await res.json();
      navigate('/induction/complete', { state: { result, name, email } });
    } catch (err) {
      setSubmitError('Your answers could not be sent. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h1>Safety Induction Assessment</h1>

      {quizStatus === 'error' ? (
        <div>
          <p>The assessment could not be loaded. Please check your connection and try again.</p>
          <button type="button" onClick={loadQuiz}>Try again</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {errors.length > 0 && (
            <div
              ref={summaryRef}
              role="alert"
              tabIndex={-1}
              style={{
                border: '2px solid #c53030',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                backgroundColor: '#ffe6e6'
              }}
            >
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Please fix the following before submitting:</strong>
              </p>
              <ul style={{ margin: 0 }}>
                {errors.map((problem) => (
                  <li key={problem.id}>
                    <a href={`#${problem.id}`} onClick={focusField(problem.id)}>{problem.message}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="name">Name</label><br />
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={inputStyle}
              aria-invalid={errorFor('name') ? 'true' : 'false'}
              aria-describedby={errorFor('name') ? 'name-error' : undefined}
            />
            {errorFor('name') && <p id="name-error" style={errorTextStyle}>{errorFor('name').message}</p>}
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="email">Email address</label><br />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              aria-invalid={errorFor('email') ? 'true' : 'false'}
              aria-describedby={errorFor('email') ? 'email-error' : undefined}
            />
            {errorFor('email') && <p id="email-error" style={errorTextStyle}>{errorFor('email').message}</p>}
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="phone">Phone number</label><br />
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={inputStyle}
              aria-invalid={errorFor('phone') ? 'true' : 'false'}
              aria-describedby={errorFor('phone') ? 'phone-error' : undefined}
            />
            {errorFor('phone') && <p id="phone-error" style={errorTextStyle}>{errorFor('phone').message}</p>}
          </div>

          <div className="visually-hidden">
            <label htmlFor="website">Leave this field blank</label>
            <input
              id="website"
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          {quizStatus === 'loading' && <p>Loading the assessment…</p>}

          {quizStatus === 'ready' && quiz && (
            <>
              <p>
                {quiz.totalQuestions} questions. You need {quiz.passMark} correct to pass. There is no
                time limit and no limit on attempts.
              </p>

              {quiz.questions.map((question, index) => {
                const problem = errorFor(`question-${question.id}`);
                return (
                  <fieldset
                    key={question.id}
                    id={`question-${question.id}`}
                    tabIndex={-1}
                    style={{
                      marginBottom: '20px',
                      padding: '15px',
                      borderRadius: '8px',
                      border: problem ? '2px solid #c53030' : '1px solid #ccc'
                    }}
                  >
                    <legend>{index + 1}. {question.text}</legend>
                    {problem && <p style={errorTextStyle}>Select an answer.</p>}
                    {question.options.map((option) => (
                      <label
                        key={option.key}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '44px' }}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option.key}
                          checked={answers[question.id] === option.key}
                          onChange={() => handleAnswerChange(question.id, option.key)}
                        />
                        {option.text}
                      </label>
                    ))}
                  </fieldset>
                );
              })}

              <p>
                Your name, email address and phone number are sent to the Brighton
                Icebergers committee along with your result, so they can record your
                induction. They are not used for anything else and are not shared outside
                the club.
              </p>

              {submitError && <p role="alert" style={errorTextStyle}>{submitError}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="external-link-btn"
                style={{ opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer', border: 'none' }}
              >
                {submitting ? 'Submitting…' : 'Submit assessment'}
              </button>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default InductionAssessment;
